import { useEffect, useRef, useState, type FormEvent } from "react";
import { Button, Field, Screen, Spinner, Text, useDelayedFlag } from "@ferrivia/ui";
import {
  tryPasskeySignIn,
  useApi,
  useEmailOtpSignIn,
  usePasskeyDevice,
  useTracker,
  type SessionCredentials,
} from "@ferrivia/auth-web";
import { useTranslation } from "@ferrivia/i18n";
import s from "./LoginScreen.module.css";
import { useScreenBackground } from "../../hooks/useScreenBackground";
import { InstallPrompt } from "../InstallPrompt";

export interface EmailOtpLoginScreenProps {
  initialErrorCode?: string | null;
  onSignedIn: (credentials: SessionCredentials) => void;
}

export function EmailOtpLoginScreen({ onSignedIn }: EmailOtpLoginScreenProps) {
  const tracker = useTracker();
  const api = useApi();
  const passkeyDevice = usePasskeyDevice();
  const { t } = useTranslation("client");
  const otp = useEmailOtpSignIn({ onSignedIn, track: tracker });
  const [emailInput, setEmailInput] = useState("");
  const [codeInput, setCodeInput] = useState("");
  const [passkeyPending, setPasskeyPending] = useState(false);
  const [passkeyError, setPasskeyError] = useState<string | null>(null);
  // A passkey is only tried first on a browser that already holds one; a
  // brand-new visitor goes straight to the email form and never meets a
  // passkey picker.
  const [passkeyFirst, setPasskeyFirst] = useState(() => passkeyDevice.lastCredential() !== null);
  const [expanded, setExpanded] = useState(false);
  const pending = otp.pending || passkeyPending;
  const showSpinner = useDelayedFlag(pending, 400);
  const canTryPasskey = typeof PublicKeyCredential !== "undefined";

  useScreenBackground("login", "--color-surface-default");

  const trackerRef = useRef(tracker);
  trackerRef.current = tracker;

  useEffect(() => {
    trackerRef.current.track("signin.screen_viewed", {});
  }, []);

  async function signInWithPasskey() {
    setPasskeyPending(true);
    setPasskeyError(null);
    tracker.track("signin.started", {});
    const credentials = await tryPasskeySignIn({ api, passkeyDevice, track: tracker });
    if (credentials) {
      onSignedIn(credentials);
      return;
    }
    setPasskeyPending(false);
    setPasskeyFirst(false);
    setExpanded(true);
    setPasskeyError(
      t("login.otp.passkeyFailed", "Passkeyta ei löytynyt tältä laitteelta. Kirjaudu sähköpostikoodilla."),
    );
  }

  function handleEmailSubmit(event: FormEvent) {
    event.preventDefault();
    setPasskeyError(null);
    void otp.requestCode(emailInput);
  }

  function handleCodeSubmit(event: FormEvent) {
    event.preventDefault();
    void otp.submitCode(codeInput);
  }

  function handleSignInClick() {
    if (passkeyFirst) {
      void signInWithPasskey();
      return;
    }
    setExpanded(true);
  }

  const error = otp.error ?? passkeyError;
  const isExpanded = expanded || otp.step !== "email";

  return (
    <Screen className={`${s.root} ${s.otp} ${isExpanded ? s.expanded : ""}`}>
      <div className={s.brandmark}>
        <Text as="h1" size="3xl" className={s.name}>
          Ferrivia
        </Text>
        <div className={s.caption} aria-hidden={isExpanded}>
          <div className={s.captionInner}>
            <div className={s.rule} aria-hidden="true" />
            <Text as="p" className={s.tagline}>
              {t("login.tagline", "Raudanpuutteen digiklinikka")}
            </Text>
          </div>
        </div>
      </div>

      <div className={s.lower}>
      {!isExpanded ? (
        <div className={s.actions}>
          <Button className={s.submit} disabled={pending} onClick={handleSignInClick}>
            {showSpinner ? <Spinner size={16} /> : null}
            {t("login.submit", "Kirjaudu sisään")}
          </Button>
          {passkeyFirst ? (
            <Button
              variant="ghost"
              disabled={pending}
              onClick={() => {
                setPasskeyFirst(false);
                setExpanded(true);
              }}
            >
              {t("login.otp.useEmail", "Käytä sähköpostia")}
            </Button>
          ) : null}
        </div>
      ) : otp.step === "email" ? (
        <form className={`${s.actions} ${s.form}`} onSubmit={handleEmailSubmit}>
          <Field
            label={t("login.otp.emailLabel", "Sähköposti")}
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            required
            autoFocus
            value={emailInput}
            onChange={(event) => setEmailInput(event.target.value)}
          />
          <Button className={s.submit} type="submit" disabled={pending || emailInput.trim() === ""}>
            {showSpinner ? <Spinner size={16} /> : null}
            {t("login.otp.sendCode", "Lähetä koodi")}
          </Button>
          {canTryPasskey ? (
            <Button variant="ghost" type="button" disabled={pending} onClick={() => void signInWithPasskey()}>
              {t("login.otp.usePasskey", "Käytä passkeyta")}
            </Button>
          ) : null}
        </form>
      ) : (
        <form className={`${s.actions} ${s.form}`} onSubmit={handleCodeSubmit}>
          <Text as="p" className={s.tagline}>
            {t("login.otp.codeSent", "Lähetimme kuusinumeroisen koodin osoitteeseen {{email}}.", { email: otp.email })}
          </Text>
          <Field
            label={t("login.otp.codeLabel", "Koodi")}
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]{6}"
            maxLength={6}
            required
            autoFocus
            value={codeInput}
            onChange={(event) => setCodeInput(event.target.value.replace(/\D/g, ""))}
          />
          <Button className={s.submit} type="submit" disabled={pending || codeInput.length !== 6}>
            {showSpinner ? <Spinner size={16} /> : null}
            {t("login.otp.verify", "Kirjaudu sisään")}
          </Button>
          <Button variant="ghost" type="button" disabled={pending} onClick={() => void otp.resend()}>
            {t("login.otp.resend", "Lähetä koodi uudelleen")}
          </Button>
          <Button
            variant="ghost"
            type="button"
            disabled={pending}
            onClick={() => {
              setCodeInput("");
              otp.changeEmail();
            }}
          >
            {t("login.otp.changeEmail", "Vaihda sähköpostiosoite")}
          </Button>
        </form>
      )}

      {error ? (
        <Text as="p" size="sm" className={s.error}>
          {error}
        </Text>
      ) : null}
      </div>
      <InstallPrompt />
    </Screen>
  );
}
